import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowRight, CalendarClock, Package, ClipboardList, Repeat, MessagesSquare, Send, Layers, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import ProviderDashboardLayout from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import StatusPill from "@/components/marketplace/StatusPill";

export default function DashboardHomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, b, o, p, sv] = await Promise.all([
          api.get("/dashboard/transactions/summary"),
          api.get("/bookings/mine?role=customer"),
          api.get("/digital-orders/mine?role=customer"),
          api.get("/providers/me/profile").catch(() => ({ data: null })),
          api.get("/services/mine/list").catch(() => ({ data: [] })),
        ]);
        setSummary(s.data || {});
        setBookings((b.data || []).slice(0, 5));
        setOrders((o.data || []).slice(0, 5));
        setProfile(p.data);
        setServices(sv.data || []);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  const c = summary?.customer || {};
  const pv = summary?.provider;

  return (
    <ProviderDashboardLayout>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Dashboard</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">Hey, {user?.display_name?.split(" ")[0] || "there"}.</h1>
          <p className="text-[#252A34]/70 mt-1 text-sm">Your Gulf Coast Work command center.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/request-a-service" className="inline-flex items-center gap-2 rounded-full bg-[#071b22] text-white px-4 py-2.5 text-sm font-medium hover:bg-black" data-testid="quick-post-request"><ClipboardList className="h-4 w-4" /> Request a service</Link>
          <Link to="/book-local" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-medium hover:bg-slate-50"><CalendarClock className="h-4 w-4" /> Book local</Link>
          <Link to="/hire-digital" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-medium hover:bg-slate-50"><Package className="h-4 w-4" /> Hire digital</Link>
        </div>
      </div>

      {/* Customer summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="dashboard-stats-customer">
        <StatCard icon={CalendarClock} label="Active bookings" value={c.active_bookings || 0} to="/dashboard/bookings" />
        <StatCard icon={Package} label="Active orders" value={c.active_orders || 0} to="/dashboard/orders" />
        <StatCard icon={ClipboardList} label="Open requests" value={c.open_requests || 0} to="/dashboard/requests" />
        <StatCard icon={Repeat} label="Recurring" value={summary?.provider?.active_recurring || 0} to="/dashboard/recurring" />
        <StatCard icon={MessagesSquare} label="Unread messages" value={summary?.unread_messages || 0} to="/dashboard/inbox" accent="amber" />
      </div>

      {/* Provider summary */}
      {pv && (
        <div className="mb-8">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71] mb-3">Provider</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-stats-provider">
            <StatCard icon={CalendarClock} label="Booked jobs" value={pv.active_bookings} accent="emerald" to="/dashboard/bookings" />
            <StatCard icon={Package} label="Order queue" value={pv.active_orders} accent="emerald" to="/dashboard/orders" />
            <StatCard icon={Send} label="Live proposals" value={pv.active_proposals} accent="emerald" to="/dashboard/find-work" />
            <StatCard icon={Layers} label="Published services" value={services.filter((s) => s.status === "published").length} accent="emerald" to="/dashboard/services" />
          </div>
        </div>
      )}

      {!profile && (
        <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-6" data-testid="no-provider-profile">
          <div className="font-display font-semibold text-amber-900">Want to earn on Gulf Coast Work?</div>
          <div className="text-sm text-amber-900/80 mt-1">Create a provider profile to publish services and receive proposals.</div>
          <Link to="/become-a-provider" className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-medium hover:bg-amber-600">
            Start onboarding <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl text-[#071b22] font-semibold">Recent bookings</h2>
            <Link to="/dashboard/bookings" className="text-sm font-medium text-[#165a71] inline-flex items-center gap-1">See all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          {bookings.length === 0 ? (
            <EmptyRow title="No bookings yet." cta="Book local" href="/book-local" />
          ) : (
            <div className="space-y-2">
              {bookings.map((b, i) => (
                <motion.div key={b.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} data-testid={`home-booking-${b.id}`}>
                  <Link to={`/dashboard/bookings/${b.id}`} className="block rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#165a71]/40">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-display font-medium text-[#071b22] truncate">{b.service_title}</div>
                      <StatusPill status={b.status} />
                    </div>
                    <div className="text-xs text-[#252A34]/60 mt-1">{b.scheduled_start ? new Date(b.scheduled_start).toLocaleString() : "—"} · ${(b.total_estimate || 0).toFixed(2)}</div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </section>
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl text-[#071b22] font-semibold">Recent digital orders</h2>
            <Link to="/dashboard/orders" className="text-sm font-medium text-[#165a71] inline-flex items-center gap-1">See all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          {orders.length === 0 ? (
            <EmptyRow title="No digital orders yet." cta="Hire digital" href="/hire-digital" />
          ) : (
            <div className="space-y-2">
              {orders.map((o, i) => (
                <motion.div key={o.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} data-testid={`home-order-${o.id}`}>
                  <Link to={`/dashboard/orders/${o.id}`} className="block rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#165a71]/40">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-display font-medium text-[#071b22] truncate">{o.service_title}</div>
                      <StatusPill status={o.status} />
                    </div>
                    <div className="text-xs text-[#252A34]/60 mt-1">{o.due_date ? `Due ${new Date(o.due_date).toLocaleDateString()}` : "—"} · ${(o.total_estimate || 0).toFixed(2)}</div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </section>
      </div>
    </ProviderDashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, accent, to }) {
  const accentClass = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
  }[accent] || "text-[#165a71] bg-[#165a71]/10 border-[#165a71]/20";
  const inner = (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#165a71]/40 transition-colors">
      <div className={`inline-flex h-9 w-9 rounded-xl items-center justify-center ${accentClass} border`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-3 font-display text-2xl font-semibold text-[#071b22]">{value}</div>
      <div className="text-xs text-[#252A34]/60 mt-0.5 uppercase tracking-wider">{label}</div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function EmptyRow({ title, cta, href }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center">
      <div className="text-[#252A34]/70 text-sm">{title}</div>
      <Link to={href} className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium">
        {cta} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
